import { ThumbsUp, ThumbsDown, Loader2, Copy, Save, Check, Send, X } from 'lucide-react';
import { Report } from '../../../lib/types';
import { FeedbackState } from '../types';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import CommentBox from "./CommentBox"
import YesNoButtons from "./YesNoButtons"
import { SaveAnalysisModal } from './saveModal';

interface ReportListProps {
    reports: Report[];
    isLoading: boolean;
    feedback: FeedbackState;
    onFeedbackClick: (reportId: string, isPositive: boolean) => void;
    onCommentSubmit: (reportId: string) => void;
    onCopyText: (text: string, reportId: string) => void;
    onSaveReport: (reportId: string, name: string) => Promise<boolean>;
    setFeedback: React.Dispatch<React.SetStateAction<FeedbackState>>;
    onResponse: (response: string) => void;
    setReport: React.Dispatch<React.SetStateAction<Report[]>>

}

export const ReportList = ({
    reports,
    isLoading,
    feedback,
    onFeedbackClick,
    onCommentSubmit,
    onCopyText,
    onSaveReport,
    setFeedback,
    onResponse,
    setReport
}: ReportListProps) => {
    const { t } = useTranslation();
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [savedId, setSavedId] = useState<string | null>(null);
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);


    const needsYesNoResponse = (item: Report) => {
        const step = item.step || [];
        return (
            (step[0] === 1 && step[1] === 2) ||
            (step[0] === 1 && step[1] === 3)
        );
    };


    const handleOpenModal = (reportId: string) => {
        setIsSaveModalOpen(true)
        setSavedId(reportId);

    }

    const handleCopy = (text: string, reportId: string) => {
        onCopyText(text, reportId);
        setCopiedId(reportId);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const handleSaveAndNew = async (name: string): Promise<boolean> => {
        if (!savedId) return false;

        const success = await onSaveReport(savedId, name);
        if (success) {
            setTimeout(() => setSavedId(null), 2000);
            setReport([]);
        }
        return success;
    };


    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
        );
    }

    return (
        <>

            <div className="space-y-4">
                {reports.map((item: Report, index) => (
                    <div key={item._id || index}>
                        {!needsYesNoResponse(item) && item.prompt &&
                            <div className="relative bg-blue-100 p-3 mb-2 rounded-tl-xl rounded-br-xl">
                                <div className="whitespace-pre-wrap text-sm text-gray-600 ">
                                    {item.prompt}
                                </div>
                            </div>
                        }
                        {item.text && (
                            <div className="relative bg-gray-200 p-3 rounded-tr-xl rounded-bl-xl">
                                <div className="whitespace-pre-wrap text-sm text-gray-600">
                                    {item.text}
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                    {((index === reports.length - 1 || index === reports.length - 2) && !feedback[item._id]?.submitted) && (
                                        <>
                                            <button
                                                onClick={() => onFeedbackClick(item._id, true)}
                                                className={`p-1 rounded-full transition-colors ${feedback[item._id]?.isPositive === true
                                                    ? 'bg-blue-200 text-blue-600'
                                                    : 'hover:bg-blue-100 text-gray-600'
                                                    }`}
                                                title={t('features.swimlane.likeReport')}
                                            >
                                                <ThumbsUp
                                                    size={18}
                                                    fill={feedback[item._id]?.isPositive === true ? "currentColor" : "none"}
                                                />
                                            </button>
                                            <button
                                                onClick={() => onFeedbackClick(item._id, false)}
                                                className={`p-1 rounded-full transition-colors ${feedback[item._id]?.isPositive === false
                                                    ? 'bg-red-200 text-red-400'
                                                    : 'hover:bg-red-100 text-gray-600'
                                                    }`}
                                                title={t('features.swimlane.dontLikeReport')}
                                            >
                                                <ThumbsDown
                                                    size={18}
                                                    fill={feedback[item._id]?.isPositive === false ? "currentColor" : "none"}
                                                />
                                            </button>
                                        </>
                                    )}
                                    {(index === reports.length - 1 || index === reports.length - 2) &&
                                        <div className="ml-auto flex gap-2">
                                            <button
                                                onClick={() => handleCopy(item.text, item._id)}
                                                className={`p-1 rounded-full transition-all transform ${copiedId === item._id
                                                    ? 'bg-green-200 text-green-600 scale-110'
                                                    : 'hover:bg-gray-300 text-gray-600'
                                                    }`}
                                                title={t('words.copy')}
                                            >
                                                {copiedId === item._id ? (
                                                    <Check size={18} className="animate-pulse" />
                                                ) : (
                                                    <Copy size={18} />
                                                )}
                                            </button>
                                            {!needsYesNoResponse(item) &&
                                                <button
                                                    onClick={() => handleOpenModal(item._id)}
                                                    className={`p-1 rounded-full transition-all transform ${savedId === item._id
                                                        ? 'hover:bg-gray-300 text-gray-600'//bg-green-200 text-green-600 scale-110
                                                        : 'hover:bg-gray-300 text-gray-600'
                                                        }`}
                                                    title={t('words.save')}
                                                >
                                                    {/* {savedId === item._id ? (
                                                        <Check size={18} className="animate-pulse" />
                                                    ) : (
                                                        <Save size={18} />
                                                    )} */}
                                                    <Save size={18} />
                                                </button>
                                            }
                                        </div>}
                                </div>
                                {needsYesNoResponse(item) && index === reports.length - 1 && (
                                    <YesNoButtons onResponse={onResponse} t={t} />
                                )}
                            </div>
                        )}

                        {feedback[item._id]?.showCommentInput && !feedback[item._id]?.submitted && (
                            <CommentBox
                                value={feedback[item._id]?.comment || ''}
                                placeholder={t('words.comments') + '...'}
                                onChange={(newValue) =>
                                    setFeedback(prev => ({
                                        ...prev,
                                        [item._id]: { ...prev[item._id], comment: newValue }
                                    }))
                                }
                                onSubmit={() => onCommentSubmit(item._id)}
                                onCancel={() => {
                                    setFeedback(prev => {
                                        const newFeedback = { ...prev };
                                        delete newFeedback[item._id];
                                        return newFeedback;
                                    });
                                }}
                            />

                        )}
                    </div>
                ))}
            </div>

            <SaveAnalysisModal
                isOpen={isSaveModalOpen}
                onClose={() => setIsSaveModalOpen(false)}
                onSave={handleSaveAndNew}
            />
        </>
    );
};